import React, { useEffect, useState } from 'react';
import { ArrowLeft, Building2, CheckCircle, Database, Eye, EyeOff, Info, Key, Save, Settings } from 'lucide-react';

const DEFAULT_CONFIG = {
  enabled: true,
  customerShortName: '',
  companyCode: '',
  propertyCode: '',
  clientIdSecretName: '',
  clientIdSecretValue: '',
  clientSecretSecretName: '',
  clientSecretSecretValue: '',
  historicalDays: 90,
  // M3 Budget Forecast API v2 (all off by default)
  budgetPull: false,
  budgetPush: false,
  forecastPull: false,
  forecastPush: false,
  forecastName: 'Lodgeic Forecast'
};

const SYNC_OPTIONS = [
  { field: 'budgetPull', label: 'Pull budgets from M3', help: 'M3 is the budget of record: its annual budget loads into Lodgeic nightly (GL accounts only).', excludes: 'budgetPush' },
  { field: 'budgetPush', label: 'Push budgets to M3', help: 'The published Lodgeic budget is sent to M3 whenever it changes.', excludes: 'budgetPull' },
  { field: 'forecastPull', label: 'Pull forecasts from M3', help: 'Loads the newest M3 forecast (matching the forecast name below if set).', excludes: 'forecastPush' },
  { field: 'forecastPush', label: 'Push forecasts to M3', help: 'The latest published Lodgeic forecast is sent to M3 under the forecast name below.', excludes: 'forecastPull' }
];

// Lodgeic's own M3 integration credentials authorize every M3 customer we
// onboard, so one shared pair of Key Vault secrets is the default. A customer
// that issues its own credentials gets per-customer secret names instead.
const SHARED_SECRET_NAMES = {
  clientIdSecretName: 'm3-client-id',
  clientSecretSecretName: 'm3-client-secret'
};

const isSharedCredentials = (config) => {
  const idName = (config?.clientIdSecretName || '').trim();
  const secretName = (config?.clientSecretSecretName || '').trim();
  return (!idName && !secretName)
    || (idName === SHARED_SECRET_NAMES.clientIdSecretName && secretName === SHARED_SECRET_NAMES.clientSecretSecretName);
};

const suggestedSecretName = (customerShortName, suffix) => {
  const shortName = (customerShortName || '').trim().toLowerCase().replace(/[^a-z0-9-]+/g, '-');
  return shortName ? `m3-${shortName}-${suffix}` : '';
};

const M3Config = ({ selectedLocation, onSave, onBack, isLoading, error, initialConfig = null }) => {
  const [m3Config, setM3Config] = useState(DEFAULT_CONFIG);
  const [credentialMode, setCredentialMode] = useState('shared');
  const [glScope, setGlScope] = useState('company');
  const [validationErrors, setValidationErrors] = useState({});
  const [touched, setTouched] = useState({});
  const [showSecrets, setShowSecrets] = useState({
    clientId: false,
    clientSecret: false
  });

  useEffect(() => {
    if (!initialConfig?.m3Config) return;
    const existing = initialConfig.m3Config;
    setM3Config({
      ...DEFAULT_CONFIG,
      ...existing,
      propertyCode: existing.propertyCode || ''
    });
    setCredentialMode(isSharedCredentials(existing) ? 'shared' : 'customer');
    setGlScope(existing.propertyCode ? 'property' : 'company');
  }, [initialConfig]);

  const updateField = (field, value) => {
    setM3Config(prev => {
      const next = { ...prev, [field]: value };
      // pull and push of the same kind overwrite each other nightly
      const excludes = SYNC_OPTIONS.find(o => o.field === field)?.excludes;
      if (value === true && excludes) next[excludes] = false;
      if (field === 'customerShortName' && credentialMode === 'customer') {
        // Suggest the per-customer secret names, but never clobber a name the
        // user typed themselves — only fill fields that are empty or still
        // carry the suggestion for the previous short name.
        if (!prev.clientIdSecretName.trim() || prev.clientIdSecretName === suggestedSecretName(prev.customerShortName, 'client-id')) {
          next.clientIdSecretName = suggestedSecretName(value, 'client-id');
        }
        if (!prev.clientSecretSecretName.trim() || prev.clientSecretSecretName === suggestedSecretName(prev.customerShortName, 'client-secret')) {
          next.clientSecretSecretName = suggestedSecretName(value, 'client-secret');
        }
      }
      return next;
    });
    if (validationErrors[field]) {
      setValidationErrors(prev => ({ ...prev, [field]: null }));
    }
  };

  const selectCredentialMode = (mode) => {
    setCredentialMode(mode);
    setM3Config(prev => (mode === 'customer'
      ? {
          ...prev,
          clientIdSecretName: isSharedCredentials(prev) ? suggestedSecretName(prev.customerShortName, 'client-id') : prev.clientIdSecretName,
          clientSecretSecretName: isSharedCredentials(prev) ? suggestedSecretName(prev.customerShortName, 'client-secret') : prev.clientSecretSecretName
        }
      : { ...prev, clientIdSecretValue: '', clientSecretSecretValue: '' }));
    setValidationErrors(prev => ({ ...prev, clientIdSecretName: null, clientSecretSecretName: null }));
  };

  const selectGlScope = (scope) => {
    setGlScope(scope);
    setValidationErrors(prev => ({ ...prev, propertyCode: null }));
  };

  const validateField = (field, value) => {
    let fieldError = null;
    const trimmed = typeof value === 'string' ? value.trim() : value;

    switch (field) {
      case 'customerShortName':
        if (!trimmed) fieldError = 'Customer short name is required';
        break;
      case 'companyCode':
        if (!trimmed) fieldError = 'Company code is required';
        break;
      case 'propertyCode':
        if (glScope === 'property' && !trimmed) fieldError = 'Property code is required when importing one property';
        break;
      case 'clientIdSecretName':
        if (credentialMode === 'customer' && !trimmed) fieldError = 'Client ID secret name is required';
        break;
      case 'clientSecretSecretName':
        if (credentialMode === 'customer' && !trimmed) fieldError = 'Client secret secret name is required';
        break;
      case 'historicalDays':
        if (!Number.isInteger(Number(value)) || Number(value) < 1) {
          fieldError = 'Historical days must be a positive whole number';
        }
        break;
      case 'forecastName':
        if ((trimmed || '').length > 50) fieldError = 'Forecast name must be 50 characters or fewer (M3 limit)';
        else if (!trimmed && m3Config.forecastPush) fieldError = 'Forecast name is required to push forecasts';
        break;
      default:
        break;
    }

    setValidationErrors(prev => ({ ...prev, [field]: fieldError }));
    return fieldError;
  };

  const handleBlur = (field) => {
    setTouched(prev => ({ ...prev, [field]: true }));
    validateField(field, m3Config[field]);
  };

  const validateAll = () => {
    const fields = ['customerShortName', 'companyCode', 'propertyCode', 'clientIdSecretName', 'clientSecretSecretName', 'historicalDays', 'forecastName'];
    const errors = fields.reduce((acc, field) => ({ ...acc, [field]: validateField(field, m3Config[field]) }), {});
    setTouched(fields.reduce((acc, field) => ({ ...acc, [field]: true }), {}));
    return Object.values(errors).every(value => !value);
  };

  const handleSave = () => {
    if (!validateAll()) return;
    const customerCredentials = credentialMode === 'customer';
    onSave({
      enabled: m3Config.enabled,
      customerShortName: m3Config.customerShortName.trim(),
      companyCode: m3Config.companyCode.trim(),
      propertyCode: glScope === 'property' ? m3Config.propertyCode.trim() : null,
      ...(customerCredentials
        ? {
            clientIdSecretName: m3Config.clientIdSecretName.trim(),
            clientSecretSecretName: m3Config.clientSecretSecretName.trim(),
            ...(m3Config.clientIdSecretValue.trim() ? { clientIdSecretValue: m3Config.clientIdSecretValue.trim() } : {}),
            ...(m3Config.clientSecretSecretValue.trim() ? { clientSecretSecretValue: m3Config.clientSecretSecretValue.trim() } : {})
          }
        : SHARED_SECRET_NAMES),
      historicalDays: Number(m3Config.historicalDays),
      budgetPull: m3Config.budgetPull === true,
      budgetPush: m3Config.budgetPush === true,
      forecastPull: m3Config.forecastPull === true,
      forecastPush: m3Config.forecastPush === true,
      forecastName: (m3Config.forecastName || '').trim() || DEFAULT_CONFIG.forecastName
    });
  };

  const isFormValid = m3Config.customerShortName.trim()
    && m3Config.companyCode.trim()
    && (glScope === 'company' || m3Config.propertyCode.trim())
    && (credentialMode === 'shared' || (m3Config.clientIdSecretName.trim() && m3Config.clientSecretSecretName.trim()))
    && !Object.values(validationErrors).some(Boolean);

  const inputClass = (field) => `cp-input${touched[field] && validationErrors[field] ? ' cp-invalid' : ''}`;

  return (
    <div className="ep-config-form">
      <div className="flex items-center space-x-2">
        <Database className="w-5 h-5 text-blue-600" />
        <h2 className="ep-config-title">M3 Accounting Integration</h2>
      </div>

      {selectedLocation && (
        <div className="ep-panel">
          <p className="cp-help">
            Configuring M3 integration for: <strong>{selectedLocation.name}</strong> ({selectedLocation.code})
          </p>
        </div>
      )}

      <div className="ep-banner ep-tone-info">
        <div className="flex items-start space-x-3">
          <Info className="w-5 h-5 text-blue-600 dark:text-blue-400 flex-shrink-0 mt-0.5" />
          <div className="ep-banner-body">
            <p className="font-medium mb-1">Key Vault based credentials</p>
            <p>Lodgeic's shared M3 credentials cover every M3 customer that has granted Lodgeic access, so most locations only need the customer short name and company code. Credential values live in Key Vault, never in Cosmos.</p>
          </div>
        </div>
      </div>

      {error && (
        <div className="ep-banner ep-tone-error">
          <p className="ep-banner-body">{error}</p>
        </div>
      )}

      <div className="ep-config-fields">
        <label className="flex items-start space-x-3 cursor-pointer">
          <input
            type="checkbox"
            checked={m3Config.enabled}
            onChange={(event) => updateField('enabled', event.target.checked)}
            className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
          />
          <div>
            <span className="cp-label">Enable M3 sync</span>
            <p className="cp-help">Disabled configs stay saved but will not be used by the ETL.</p>
          </div>
        </label>

        <div>
          <label className="cp-label">
            <Settings className="inline-block w-4 h-4 mr-1" />
            Customer Short Name <span className="ep-req">*</span>
          </label>
          <input
            type="text"
            value={m3Config.customerShortName}
            onChange={(event) => updateField('customerShortName', event.target.value)}
            onBlur={() => handleBlur('customerShortName')}
            placeholder="A88"
            className={inputClass('customerShortName')}
          />
          {touched.customerShortName && validationErrors.customerShortName && (
            <p className="cp-help cp-error">{validationErrors.customerShortName}</p>
          )}
          <p className="cp-help">The M3 customer (management company) these credentials were issued for.</p>
        </div>

        <div>
          <label className="cp-label">
            <Building2 className="inline-block w-4 h-4 mr-1" />
            Company Code <span className="ep-req">*</span>
          </label>
          <input
            type="text"
            value={m3Config.companyCode}
            onChange={(event) => updateField('companyCode', event.target.value)}
            onBlur={() => handleBlur('companyCode')}
            placeholder="002"
            className={inputClass('companyCode')}
          />
          {touched.companyCode && validationErrors.companyCode && (
            <p className="cp-help cp-error">{validationErrors.companyCode}</p>
          )}
          <p className="cp-help">The M3 company (legal entity) that owns this hotel's books.</p>
        </div>

        <div>
          <label className="cp-label">
            <Building2 className="inline-block w-4 h-4 mr-1" />
            What to import
          </label>
          <div className="space-y-3 mt-1">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="radio"
                name="m3-gl-scope"
                checked={glScope === 'company'}
                onChange={() => selectGlScope('company')}
                className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <div>
                <span className="cp-label">The whole company (recommended)</span>
                <p className="cp-help">Every GL line in the company, including balance-sheet lines, accruals and manual journals, which M3 posts without a property. Use this when the company is one hotel, even if M3 lists a small outlet (a Starbucks, a bar) as a second property.</p>
              </div>
            </label>
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="radio"
                name="m3-gl-scope"
                checked={glScope === 'property'}
                onChange={() => selectGlScope('property')}
                className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <div>
                <span className="cp-label">One property within the company</span>
                <p className="cp-help">Only when one M3 company holds several hotels that each have their own Lodgeic location. Lines M3 posts at company level (balance sheet, accruals) are then left out.</p>
              </div>
            </label>
            {glScope === 'property' && (
              <div className="ml-7">
                <label className="cp-label">Property Code <span className="ep-req">*</span></label>
                <input
                  type="text"
                  value={m3Config.propertyCode}
                  onChange={(event) => updateField('propertyCode', event.target.value)}
                  onBlur={() => handleBlur('propertyCode')}
                  placeholder="002"
                  className={inputClass('propertyCode')}
                />
                {touched.propertyCode && validationErrors.propertyCode && (
                  <p className="cp-help cp-error">{validationErrors.propertyCode}</p>
                )}
              </div>
            )}
          </div>
        </div>

        <div>
          <label className="cp-label">
            <Key className="inline-block w-4 h-4 mr-1" />
            Credentials
          </label>
          <div className="space-y-3 mt-1">
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="radio"
                name="m3-credentials"
                checked={credentialMode === 'shared'}
                onChange={() => selectCredentialMode('shared')}
                className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <div>
                <span className="cp-label">Lodgeic's shared M3 credentials (recommended)</span>
                <p className="cp-help">Uses Key Vault secrets <code>{SHARED_SECRET_NAMES.clientIdSecretName}</code> / <code>{SHARED_SECRET_NAMES.clientSecretSecretName}</code>.</p>
              </div>
            </label>
            <label className="flex items-start space-x-3 cursor-pointer">
              <input
                type="radio"
                name="m3-credentials"
                checked={credentialMode === 'customer'}
                onChange={() => selectCredentialMode('customer')}
                className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300"
              />
              <div>
                <span className="cp-label">Customer-specific credentials</span>
                <p className="cp-help">Only when the customer issued its own M3 client ID and secret.</p>
              </div>
            </label>
          </div>
        </div>

        {credentialMode === 'customer' && (
        <div className="ep-grid">
          <div>
            <label className="cp-label">
              <Key className="inline-block w-4 h-4 mr-1" />
              Client ID Secret <span className="ep-req">*</span>
            </label>
            <input
              type="text"
              value={m3Config.clientIdSecretName}
              onChange={(event) => updateField('clientIdSecretName', event.target.value)}
              onBlur={() => handleBlur('clientIdSecretName')}
              placeholder="m3-a88-client-id"
              className={inputClass('clientIdSecretName')}
            />
            {touched.clientIdSecretName && validationErrors.clientIdSecretName && (
              <p className="cp-help cp-error">{validationErrors.clientIdSecretName}</p>
            )}
            <div className="relative mt-2">
              <input
                type={showSecrets.clientId ? 'text' : 'password'}
                value={m3Config.clientIdSecretValue}
                onChange={(event) => updateField('clientIdSecretValue', event.target.value)}
                placeholder="Optional. Paste client ID to create/update this Key Vault secret."
                autoComplete="new-password"
                className={`${inputClass('clientIdSecretValue')} pr-11`}
              />
              <button
                type="button"
                onClick={() => setShowSecrets(prev => ({ ...prev, clientId: !prev.clientId }))}
                className="ep-reveal"
                aria-label={showSecrets.clientId ? 'Hide client ID' : 'Show client ID'}
                title={showSecrets.clientId ? 'Hide client ID' : 'Show client ID'}
              >
                {showSecrets.clientId ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>

          <div>
            <label className="cp-label">
              <Key className="inline-block w-4 h-4 mr-1" />
              Client Secret Secret <span className="ep-req">*</span>
            </label>
            <input
              type="text"
              value={m3Config.clientSecretSecretName}
              onChange={(event) => updateField('clientSecretSecretName', event.target.value)}
              onBlur={() => handleBlur('clientSecretSecretName')}
              placeholder="m3-a88-client-secret"
              className={inputClass('clientSecretSecretName')}
            />
            {touched.clientSecretSecretName && validationErrors.clientSecretSecretName && (
              <p className="cp-help cp-error">{validationErrors.clientSecretSecretName}</p>
            )}
            <div className="relative mt-2">
              <input
                type={showSecrets.clientSecret ? 'text' : 'password'}
                value={m3Config.clientSecretSecretValue}
                onChange={(event) => updateField('clientSecretSecretValue', event.target.value)}
                placeholder="Optional. Paste client secret to create/update this Key Vault secret."
                autoComplete="new-password"
                className={`${inputClass('clientSecretSecretValue')} pr-11`}
              />
              <button
                type="button"
                onClick={() => setShowSecrets(prev => ({ ...prev, clientSecret: !prev.clientSecret }))}
                className="ep-reveal"
                aria-label={showSecrets.clientSecret ? 'Hide client secret' : 'Show client secret'}
                title={showSecrets.clientSecret ? 'Hide client secret' : 'Show client secret'}
              >
                {showSecrets.clientSecret ? <EyeOff size={18} /> : <Eye size={18} />}
              </button>
            </div>
          </div>
        </div>
        )}

        <div>
          <label className="cp-label">Historical Days</label>
          <input
            type="number"
            min="1"
            value={m3Config.historicalDays}
            onChange={(event) => updateField('historicalDays', event.target.value)}
            onBlur={() => handleBlur('historicalDays')}
            className={inputClass('historicalDays')}
          />
          {touched.historicalDays && validationErrors.historicalDays && <p className="cp-help cp-error">{validationErrors.historicalDays}</p>}
          <p className="cp-help">How far back each nightly sync re-reads the GL to pick up late postings. The first sync always imports the full history.</p>
        </div>

        <div>
          <label className="cp-label">
            <Settings className="inline-block w-4 h-4 mr-1" />
            Budgets &amp; Forecasts
          </label>
          <p className="cp-help">Optional two-way sync with the M3 Budget Forecast API. Pull and push of the same kind are mutually exclusive.</p>
          <div className="ep-grid mt-2">
            {SYNC_OPTIONS.map(option => (
              <label key={option.field} className="flex items-start space-x-3 cursor-pointer">
                <input
                  type="checkbox"
                  checked={m3Config[option.field] === true}
                  onChange={(event) => updateField(option.field, event.target.checked)}
                  className="mt-1 h-4 w-4 text-blue-600 focus:ring-blue-500 border-gray-300 rounded"
                />
                <div>
                  <span className="cp-label">{option.label}</span>
                  <p className="cp-help">{option.help}</p>
                </div>
              </label>
            ))}
          </div>
        </div>

        {(m3Config.forecastPull || m3Config.forecastPush) && (
          <div>
            <label className="cp-label">Forecast Name{m3Config.forecastPush && <span className="ep-req"> *</span>}</label>
            <input
              type="text"
              maxLength={50}
              value={m3Config.forecastName}
              onChange={(event) => updateField('forecastName', event.target.value)}
              onBlur={() => handleBlur('forecastName')}
              placeholder="Lodgeic Forecast"
              className={inputClass('forecastName')}
            />
            {touched.forecastName && validationErrors.forecastName && <p className="cp-help cp-error">{validationErrors.forecastName}</p>}
            <p className="cp-help">
              {m3Config.forecastPush
                ? 'The forecast name Lodgeic publishes under in M3 (re-posting the same name and effective date updates it).'
                : 'Leave blank to pull whichever M3 forecast has the newest effective date; set it to pin a specific forecast.'}
            </p>
          </div>
        )}
      </div>

      {isFormValid && (
        <div className="ep-banner ep-tone-ok">
          <div className="flex items-center space-x-2">
            <CheckCircle className="w-5 h-5 text-green-600 dark:text-green-400" />
            <p className="ep-banner-body">Configuration is valid and ready to save</p>
          </div>
        </div>
      )}

      <div className="ep-config-foot">
        <button
          onClick={onBack}
          disabled={isLoading}
          className="cp-btn"
        >
          <ArrowLeft className="mr-2" size={16} />
          Back to Setup
        </button>

        <button
          onClick={handleSave}
          disabled={isLoading || !isFormValid}
          className="cp-btn cp-primary"
        >
          {isLoading ? (
            <>
              <div className="animate-spin rounded-full h-4 w-4 border-b-2 border-white mr-2"></div>
              Saving...
            </>
          ) : (
            <>
              <Save className="mr-2" size={16} />
              Save Configuration
            </>
          )}
        </button>
      </div>
    </div>
  );
};

export default M3Config;
